import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Lock, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";

/**
 * UpgradeGateModal — listens for global `pjp:pro-required` events (dispatched
 * by axios interceptor on HTTP 402) and pops a friendly upgrade prompt. This
 * lets every locked endpoint use the same UX without each caller building it.
 */
export default function UpgradeGateModal() {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const handler = (e) => setDetail(e.detail || {});
    window.addEventListener("pjp:pro-required", handler);
    return () => window.removeEventListener("pjp:pro-required", handler);
  }, []);

  if (!detail) return null;

  const feature = detail.feature || "This feature";
  const upgradeUrl = detail.upgrade_url || "/membership/addon?tier=pro";
  const close = () => setDetail(null);

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="upgrade-gate-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-200"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-cyan/40 bg-obsidian/95 p-7 overflow-hidden shadow-[0_0_60px_-10px_rgba(75,156,255,0.5)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Ambient glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at 100% 0%, rgba(75,156,255,0.20), transparent 55%), radial-gradient(circle at 0% 100%, rgba(255,138,61,0.10), transparent 55%)" }}
        />
        {/* HUD corners */}
        <span className="hud-c-tl" /><span className="hud-c-tr" />
        <span className="hud-c-bl" /><span className="hud-c-br" />

        <button
          type="button"
          onClick={close}
          data-testid="upgrade-gate-close"
          className="absolute top-3 right-3 h-8 w-8 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-white/30 transition-colors z-10"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="h-4 w-4 text-cyan" />
            <div className="mono text-[10px] tracking-[0.28em] uppercase text-cyan">// PRO PERK</div>
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tighter leading-tight mb-2">
            {feature} is a Pro perk.
          </h2>
          <p className="text-white/70 text-[14px] leading-relaxed mb-5">
            Upgrade to <span className="text-cyan font-semibold">PJP Pro</span> for
            <span className="text-white font-semibold"> $129/yr</span> — that's less than
            <span className="text-white"> $11/mo</span> — and unlock every professional tool.
          </p>

          <div className="grid grid-cols-2 gap-2 mb-6">
            {[
              "Job Board applications",
              "Unlimited AeroIntel",
              "Free JumpSeat access",
              "Direct messaging",
              "Crew Directory contacts",
              "Fleet Live Map",
              "Salary Insights",
              "Community Documents",
            ].map((p) => (
              <div key={p} className="flex items-center gap-1.5 text-[12px] text-white/70">
                <ShieldCheck className="h-3 w-3 text-cyan flex-shrink-0" />
                <span>{p}</span>
              </div>
            ))}
          </div>

          <Link
            to={upgradeUrl}
            data-testid="upgrade-gate-cta"
            onClick={close}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 bg-cyan text-obsidian font-display font-semibold tracking-wide shadow-[0_0_20px_-4px_rgba(75,156,255,0.7)] hover:brightness-105 transition-all"
          >
            <Sparkles className="h-4 w-4" /> UPGRADE TO PRO — $129/YR <ArrowRight className="h-4 w-4" />
          </Link>

          <button
            type="button"
            onClick={close}
            data-testid="upgrade-gate-later"
            className="mt-3 w-full text-[12px] text-white/50 hover:text-white transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
